<script>
/* global axios */
// Use Chatwoot's global, authenticated axios (carries the rotating devise-token-auth
// headers) -- NOT `import axios from 'axios'`, which is an unauthenticated copy and 401s
// on /api/v1/accounts/:id/... . Mirrors OnemsgWhatsapp.vue / dashboard/api/ApiClient.js.
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { required, helpers } from '@vuelidate/validators';
import router from '../../../../index';

import NextButton from 'dashboard/components-next/button/Button.vue';

const startsWithPlus = (value = '') => value.startsWith('+');
const senderFormat = helpers.regex(/^[A-Za-z0-9]{1,11}$/);

export default {
  components: {
    NextButton,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      inboxName: '',
      phoneNumber: '',
      source: '',
      username: '',
      token: '',
      maxSmsParts: 3,
      testState: null, // null | 'testing' | 'ok' | 'error'
      testMessage: '',
      webhookUrl: '',
      createdInboxId: null,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'inboxes/getUIFlags',
    }),
    accountId() {
      return this.$route.params.accountId;
    },
  },
  validations: {
    inboxName: { required },
    phoneNumber: { required, startsWithPlus },
    source: { required, senderFormat },
    username: { required },
    token: { required },
  },
  methods: {
    credPayload() {
      return {
        username: this.username,
        token: this.token,
        source: this.source,
      };
    },
    async testConnection() {
      this.testState = 'testing';
      this.testMessage = '';
      try {
        const { data } = await axios.post(
          `/api/v1/accounts/${this.accountId}/smarticks/telzar/test`,
          this.credPayload()
        );
        if (data.ok) {
          this.testState = 'ok';
          this.testMessage = `✅ ${this.$t('INBOX_MGMT.ADD.SMS.TELZAR.TEST.OK')}`;
        } else if (data.token_valid === false) {
          this.testState = 'error';
          this.testMessage = `❌ ${this.$t('INBOX_MGMT.ADD.SMS.TELZAR.TEST.BAD_AUTH')}`;
        } else if (data.sender_ok === false) {
          this.testState = 'error';
          this.testMessage = `❌ ${this.$t('INBOX_MGMT.ADD.SMS.TELZAR.TEST.BAD_SENDER')}`;
        } else {
          this.testState = 'error';
          this.testMessage = `❌ ${data.message || this.$t('INBOX_MGMT.ADD.SMS.TELZAR.TEST.ERROR')}`;
        }
      } catch (error) {
        this.testState = 'error';
        this.testMessage = `❌ ${this.$t('INBOX_MGMT.ADD.SMS.TELZAR.TEST.ERROR')}`;
      }
    },
    async createChannel() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }
      try {
        const smsChannel = await this.$store.dispatch('inboxes/createChannel', {
          name: this.inboxName?.trim(),
          channel: {
            type: 'sms',
            phone_number: this.phoneNumber,
            provider_config: {
              provider: 'telzar',
              username: this.username,
              token: this.token,
              source: this.source,
              max_sms_parts: Number(this.maxSmsParts) || 3,
            },
          },
        });
        await this.fetchWebhookUrl(smsChannel.id);
        if (!this.webhookUrl) {
          this.goToAgents(smsChannel.id);
        } else {
          this.createdInboxId = smsChannel.id;
        }
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.ADD.SMS.API.ERROR_MESSAGE'));
      }
    },
    async fetchWebhookUrl(inboxId) {
      try {
        const { data } = await axios.get(
          `/api/v1/accounts/${this.accountId}/smarticks/telzar/webhook_url`,
          { params: { inbox_id: inboxId } }
        );
        this.webhookUrl = data.webhook_url || '';
      } catch (error) {
        this.webhookUrl = '';
      }
    },
    goToAgents(inboxId) {
      router.replace({
        name: 'settings_inboxes_add_agents',
        params: { page: 'new', inbox_id: inboxId },
      });
    },
  },
};
</script>

<template>
  <div v-if="webhookUrl && createdInboxId" class="flex flex-col mx-0">
    <p class="font-medium">
      {{ $t('INBOX_MGMT.ADD.SMS.TELZAR.WEBHOOK.TITLE') }}
    </p>
    <p>{{ $t('INBOX_MGMT.ADD.SMS.TELZAR.WEBHOOK.HELP') }}</p>
    <woot-code :script="webhookUrl" />
    <div class="mt-4">
      <NextButton
        :label="$t('INBOX_MGMT.ADD.SMS.TELZAR.WEBHOOK.CONTINUE')"
        @click="goToAgents(createdInboxId)"
      />
    </div>
  </div>
  <form
    v-else
    class="flex flex-wrap flex-col mx-0"
    @submit.prevent="createChannel()"
  >
    <div class="flex-shrink-0 flex-grow-0">
      <label :class="{ error: v$.inboxName.$error }">
        {{ $t('INBOX_MGMT.ADD.SMS.TELZAR.INBOX_NAME.LABEL') }}
        <input
          v-model="inboxName"
          type="text"
          :placeholder="$t('INBOX_MGMT.ADD.SMS.TELZAR.INBOX_NAME.PLACEHOLDER')"
          @blur="v$.inboxName.$touch"
        />
      </label>
    </div>

    <div class="flex-shrink-0 flex-grow-0">
      <label :class="{ error: v$.phoneNumber.$error }">
        {{ $t('INBOX_MGMT.ADD.SMS.TELZAR.PHONE_NUMBER.LABEL') }}
        <input
          v-model="phoneNumber"
          type="text"
          :placeholder="
            $t('INBOX_MGMT.ADD.SMS.TELZAR.PHONE_NUMBER.PLACEHOLDER')
          "
          @blur="v$.phoneNumber.$touch"
        />
        <span v-if="v$.phoneNumber.$error" class="message">{{
          $t('INBOX_MGMT.ADD.SMS.TELZAR.PHONE_NUMBER.ERROR')
        }}</span>
      </label>
    </div>

    <div class="flex-shrink-0 flex-grow-0">
      <label :class="{ error: v$.source.$error }">
        {{ $t('INBOX_MGMT.ADD.SMS.TELZAR.SENDER.LABEL') }}
        <input
          v-model="source"
          type="text"
          :placeholder="$t('INBOX_MGMT.ADD.SMS.TELZAR.SENDER.PLACEHOLDER')"
          @blur="v$.source.$touch"
        />
        <span v-if="v$.source.$error" class="message">{{
          $t('INBOX_MGMT.ADD.SMS.TELZAR.SENDER.ERROR')
        }}</span>
        <span class="text-xs text-n-slate-11">{{
          $t('INBOX_MGMT.ADD.SMS.TELZAR.SENDER.HELP')
        }}</span>
      </label>
    </div>

    <div class="flex-shrink-0 flex-grow-0">
      <label :class="{ error: v$.username.$error }">
        {{ $t('INBOX_MGMT.ADD.SMS.TELZAR.USERNAME.LABEL') }}
        <input
          v-model="username"
          type="text"
          :placeholder="$t('INBOX_MGMT.ADD.SMS.TELZAR.USERNAME.PLACEHOLDER')"
          @blur="v$.username.$touch"
        />
      </label>
    </div>

    <div class="flex-shrink-0 flex-grow-0">
      <label :class="{ error: v$.token.$error }">
        {{ $t('INBOX_MGMT.ADD.SMS.TELZAR.TOKEN.LABEL') }}
        <input
          v-model="token"
          type="password"
          :placeholder="$t('INBOX_MGMT.ADD.SMS.TELZAR.TOKEN.PLACEHOLDER')"
          @blur="v$.token.$touch"
        />
      </label>
    </div>

    <div class="flex-shrink-0 flex-grow-0">
      <label>
        {{ $t('INBOX_MGMT.ADD.SMS.TELZAR.MAX_PARTS.LABEL') }}
        <input v-model="maxSmsParts" type="number" min="1" max="15" />
      </label>
    </div>

    <div class="flex items-center gap-2 mt-2">
      <NextButton
        type="button"
        faded
        :label="$t('INBOX_MGMT.ADD.SMS.TELZAR.TEST.BUTTON')"
        :is-loading="testState === 'testing'"
        @click="testConnection"
      />
      <span v-if="testState === 'ok'" class="text-sm text-n-teal-11">
        {{ testMessage }}
      </span>
      <span v-else-if="testState === 'error'" class="text-sm text-n-ruby-11">
        {{ testMessage }}
      </span>
    </div>

    <div class="w-full mt-4">
      <NextButton
        :is-loading="uiFlags.isCreating"
        :label="$t('INBOX_MGMT.ADD.SMS.TELZAR.SUBMIT_BUTTON')"
        type="submit"
      />
    </div>
  </form>
</template>
